/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Params, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { Plus, PencilLine } from 'lucide-react';

import PageHeader from '@components/layout/page-header';
import Loader from '@components/atoms/loaders/loader';
import Button from '@components/atoms/ui-kit/button.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';

import IamGroupDetails from '@components/back-office/customer/iam-group-details';
import CreateIAmGroup from '@components/back-office/customer/create-i-am-group';

import { useGetCustomerGroupsBoIAM } from '@utils/api/back-office/customer-bo/use-get-customer-bo-group-iam';
import { useGetCustomerIamGroup } from '@utils/api/back-office/customer-bo/use-get-group-bo';
import { useManageGroupMembers } from '@utils/api/back-office/customer-bo/use-update-members';
import { CustomerBoIAM } from '@utils/api/back-office/customer-bo/use-get-customer-bo-iam';
import { CreateGroupBoPayload } from '@utils/api/back-office/customer-bo/use-create-group';

export type SelectedGroup = {
  description: string;
  id: string | number;
  name: string;
};

type Row = {
  id: number | string;
  name: string;
  description: string;
};

export default function CustomerGroupBoIam() {
  const { t } = useTranslation();
  const { customerId } = useParams<Params>();
  const cid = Number(customerId) || 0;

  const [teamUsersData, setTeamUsersData] = useState<CustomerBoIAM[]>([]);
  const [teamUsersUpdateData, setTeamUsersUpdateData] = useState<
    CustomerBoIAM[]
  >([]);
  const [selectedGroup, setSelectedGroup] = useState<
    SelectedGroup | undefined
  >();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Single group details (when a row is selected)
  const { data: group } = useGetCustomerIamGroup(
    cid,
    Number(selectedGroup?.id)
  );

  // Groups list
  const { data: groups, isLoading } = useGetCustomerGroupsBoIAM(cid);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('name-is-required')),
    description: Yup.string().required(t('description-is-required')),
    member_ids: Yup.array().of(Yup.number())
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors }
  } = useForm<CreateGroupBoPayload>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(validationSchema as any),
    defaultValues: {
      name: group?.result.name || '',
      description: group?.result.description || '',
      member_ids: group?.result.user_ids.map(m => m) || []
    }
  });

  const manageGroupMembers = useManageGroupMembers();

  const handleAddMemberToTeam = (user: CustomerBoIAM) => {
    setTeamUsersData(prev => [...prev, user]);
  };
  const handleRemoveMemberFromTeam = (userId: string) => {
    setTeamUsersData(prev => prev.filter(u => u.id.toString() !== userId));
  };

  const handleAddMemberToUpdateTeam = (user: CustomerBoIAM) => {
    if (!teamUsersUpdateData.find(u => u.id === user.id)) {
      setTeamUsersUpdateData(prev => [...prev, user]);
      manageGroupMembers.mutate(
        {
          customerId: cid,
          groupId: Number(selectedGroup?.id),
          data: { add: [user.id] }
        },
        {
          onSuccess: () => void toast.success(t('group-updated-successfully')),
          onError: error => {
            const msg =
              error.response?.data?.message ||
              t('an-error-occurred-while-managing-group-members');
            toast.error(msg);
          }
        }
      );
    }
  };

  const handleRemoveMemberFromUpdateTeam = (userId: string) => {
    setTeamUsersUpdateData(prev => {
      const next = prev.filter(u => u.id.toString() !== userId);
      manageGroupMembers.mutate(
        {
          customerId: cid,
          groupId: Number(selectedGroup?.id),
          data: { delete: [parseInt(userId, 10)] }
        },
        {
          onSuccess: () => void toast.success(t('group-updated-successfully')),
          onError: error => {
            const msg =
              error.response?.data?.message ||
              t('an-error-occurred-while-managing-group-members');
            toast.error(msg);
          }
        }
      );
      return next;
    });
  };

  useEffect(() => {
    setTeamUsersData(teamUsersData);
  }, [teamUsersData]);

  useEffect(() => {
    if (!isEditModalOpen) {
      setValue('name', '');
      setValue('description', '');
      setValue('member_ids', []);
    }
  }, [isEditModalOpen, setValue]);

  /* ---------- Columns (DataGridV2) ---------- */
  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 100,
        minWidth: 80,
        enableSorting: false,
        cursor: 'pointer',
        enableFiltering: false,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('name'),
        field: 'name',
        minWidth: 220,
        enableSorting: false,
        cursor: 'pointer',
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('description'),
        field: 'description',
        minWidth: 260,
        enableSorting: false,
        cursor: 'pointer',
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t]
  );

  /* ---------- Rows ---------- */
  const rows = useMemo<Row[]>(() => {
    if (!groups) return [];
    return (groups.result || []).map(g => ({
      id: g.id ?? '',
      name: g.name ?? '',
      description: g.description ?? ''
    }));
  }, [groups]);

  /* ---------- Handlers ---------- */
  const handleCellClick = (row: Row) => {
    if (!row?.id) return;
    setSelectedGroup({
      id: row.id,
      name: row.name,
      description: row.description
    });
    setIsEditModalOpen(true);
  };

  /* ---------- Actions ---------- */
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />,
        intent: 'primary',
        run: row => {
          setSelectedGroup({
            id: row.id,
            name: row.name,
            description: row.description
          });
          setIsEditModalOpen(true);
        }
      }
    ],
    [t]
  );

  const menuOptions = useMemo(
    () => [
      {
        id: 'edit',
        label: t('edit'),
        icon: <PencilLine className="h-4 w-4" />
      }
    ],
    [t]
  );

  const handleSetMenuOptions = (item: Row) => {
    setSelectedGroup({
      id: item.id,
      name: item.name,
      description: item.description
    });
  };

  const handleMenuOptionClick = (optionId: string) => {
    if (!selectedGroup) return;
    if (optionId === 'edit') setIsEditModalOpen(true);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className={cn('min-w-full flex-1 min-h-0 flex flex-col gap-y-4')}>
          <PageHeader
            title={t('identity-and-access-management-groups')}
            caption={t(
              'list-of-all-the-groups-including-their-name-contact-and-actions'
            )}
            contentClassName="px-2"
          >
            <Button
              icon={<Plus width={14} />}
              onClick={() => setIsCreateModalOpen(true)}
              label={t('new-group')}
              variant="primary"
              size="small"
            />
          </PageHeader>

          <DataGridV2<Row>
            data={rows}
            columns={columns}
            actions={actions}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            totalItems={(groups as any)?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            fill
            dense
            striped
            grid={false}
            loading={isLoading}
            onCellClick={handleCellClick}
            getRowId={row => String(row.id)}
            menuOptions={menuOptions}
            setMenuOptions={handleSetMenuOptions}
            onMenuOptionClick={handleMenuOptionClick}
          />
        </div>
      )}

      {isCreateModalOpen && (
        <CreateIAmGroup
          register={register}
          control={control}
          errors={errors}
          handleAddMemberToTeam={handleAddMemberToTeam}
          handleRemoveMemberFromTeam={handleRemoveMemberFromTeam}
          teamUsersData={teamUsersData}
          isCreateModalOpen={isCreateModalOpen}
          setIsCreateModalOpen={setIsCreateModalOpen}
          handleSubmit={handleSubmit}
        />
      )}

      {isEditModalOpen && selectedGroup && (
        <IamGroupDetails
          setValue={setValue}
          register={register}
          errors={errors}
          handleAddMemberToUpdateTeam={handleAddMemberToUpdateTeam}
          handleRemoveMemberFromUpdateTeam={handleRemoveMemberFromUpdateTeam}
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          teamUsersData={teamUsersData}
          handleSubmit={handleSubmit}
          selectedGroup={selectedGroup}
        />
      )}
    </div>
  );
}
